import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  NumberInput,
  NumberInputField,
  Progress,
  Select,
  SimpleGrid,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react';
import { CameraSearchSelect } from '../common/SearchSelects';
import {
  fromDatetimeLocalValue,
  parseInfractionFilenameDate
} from '../../utils/parseInfractionFilenameDate';

const resolveBatchSize = (preset, customValue) => {
  if (preset !== 'custom') {
    const presetSize = Number(preset);
    return BATCH_SIZE_PRESETS.includes(presetSize) ? presetSize : 50;
  }
  const parsed = parseInt(customValue, 10);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(MAX_BATCH_SIZE, Math.max(MIN_BATCH_SIZE, parsed));
};

const CHUNK_SIZE = 5;
const BATCH_SIZE_PRESETS = [25, 50, 100, 150, 200];
const MIN_BATCH_SIZE = 1;
const MAX_BATCH_SIZE = 1000;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp'];

const UploadInfractionsTab = () => {
  const toast = useToast();
  const cancelRef = useRef();
  const fileInputRef = useRef();

  const [cameras, setCameras] = useState([]);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [cameraId, setCameraId] = useState('');
  const [status, setStatus] = useState('pending');
  const [createBatches, setCreateBatches] = useState(false);
  const [batchSizePreset, setBatchSizePreset] = useState('50');
  const [customBatchSize, setCustomBatchSize] = useState('75');
  const [items, setItems] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0, failed: 0 });
  const [missingDateDialogOpen, setMissingDateDialogOpen] = useState(false);
  const [manualDateMode, setManualDateMode] = useState(false);
  const [manualQueueIds, setManualQueueIds] = useState([]);
  const [manualDateIndex, setManualDateIndex] = useState(0);
  const [manualDateValue, setManualDateValue] = useState('');

  const missingDateItems = useMemo(
    () => items.filter((item) => !item.detectedAt),
    [items]
  );

  const parsedCount = items.length - missingDateItems.length;
  const currentManualItem = useMemo(() => {
    const id = manualQueueIds[manualDateIndex];
    if (!id) return null;
    return items.find((item) => item.id === id) || null;
  }, [items, manualQueueIds, manualDateIndex]);

  useEffect(() => {
    fetchCameras();
  }, []);

  useEffect(() => {
    if (status !== 'pending' && createBatches) {
      setCreateBatches(false);
    }
  }, [status, createBatches]);

  const fetchCameras = async () => {
    setLoadingCameras(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/cameras', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Erro ao buscar câmeras');
      const data = await response.json();
      setCameras(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({
        title: 'Erro ao carregar câmeras',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoadingCameras(false);
    }
  };

  const revokePreviewUrls = useCallback((list) => {
    list.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
  }, []);

  useEffect(() => () => revokePreviewUrls(items), [items, revokePreviewUrls]);

  const handleFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const nextItems = files
      .filter((file) => {
        if (ACCEPTED_IMAGE_TYPES.includes(file.type) || /\.(jpe?g|png|webp|bmp)$/i.test(file.name)) {
          return true;
        }
        return false;
      })
      .map((file, index) => {
        const detectedAt = parseInfractionFilenameDate(file.name);
        return {
          id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
          file,
          filename: file.name,
          detectedAt,
          previewUrl: URL.createObjectURL(file)
        };
      });

    if (nextItems.length === 0) {
      toast({
        title: 'Nenhuma imagem válida',
        description: 'Selecione arquivos JPG, PNG, WEBP ou BMP',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      event.target.value = '';
      return;
    }

    setItems((current) => {
      revokePreviewUrls(current);
      return nextItems;
    });
    setManualDateMode(false);
    setManualQueueIds([]);
    setManualDateIndex(0);
    setUploadProgress({ done: 0, total: 0, failed: 0 });
    event.target.value = '';
  };

  const clearSelection = () => {
    revokePreviewUrls(items);
    setItems([]);
    setManualDateMode(false);
    setManualQueueIds([]);
    setManualDateIndex(0);
    setUploadProgress({ done: 0, total: 0, failed: 0 });
  };

  const uploadChunk = async (chunk) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('camera_id', cameraId);
    formData.append('status', status);
    formData.append(
      'detected_ats',
      JSON.stringify(
        chunk.map((item) => (item.detectedAt ? item.detectedAt.toISOString() : null))
      )
    );

    chunk.forEach((item) => {
      formData.append('images', item.file, item.filename);
    });

    const response = await fetch('/api/infractions/manual-upload', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Erro ao enviar lote de imagens');
    }

    return data;
  };

  const runUpload = async (itemsToUpload) => {
    if (!cameraId) {
      toast({
        title: 'Selecione uma câmera',
        status: 'warning',
        duration: 2500,
        isClosable: true
      });
      return;
    }

    if (itemsToUpload.length === 0) {
      toast({
        title: 'Selecione imagens',
        status: 'warning',
        duration: 2500,
        isClosable: true
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress({ done: 0, total: itemsToUpload.length, failed: 0 });

    const createdIds = [];
    let failed = 0;

    try {
      for (let offset = 0; offset < itemsToUpload.length; offset += CHUNK_SIZE) {
        const chunk = itemsToUpload.slice(offset, offset + CHUNK_SIZE);
        try {
          const result = await uploadChunk(chunk);
          const created = Array.isArray(result.created) ? result.created : [];
          created.forEach((item) => createdIds.push(item.id));
          failed += Array.isArray(result.failures) ? result.failures.length : 0;
        } catch (error) {
          failed += chunk.length;
          console.error('Erro no chunk de upload:', error);
        }

        setUploadProgress({
          done: Math.min(offset + chunk.length, itemsToUpload.length),
          total: itemsToUpload.length,
          failed
        });
      }

      let batchesMessage = '';
      if (createBatches && status === 'pending' && createdIds.length > 0) {
        const token = localStorage.getItem('token');
        const batchesResponse = await fetch('/api/infractions/manual-upload/create-batches', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            camera_id: Number(cameraId),
            infraction_ids: createdIds,
            batch_size: resolveBatchSize(batchSizePreset, customBatchSize)
          })
        });
        const batchesData = await batchesResponse.json().catch(() => ({}));
        if (batchesResponse.ok) {
          const count = Array.isArray(batchesData.batches) ? batchesData.batches.length : 0;
          batchesMessage = count > 0
            ? ` ${count} lote(s) criado(s).`
            : ' Nenhum lote foi criado.';
        } else {
          batchesMessage = ` Falha ao criar lotes: ${batchesData.message || 'erro desconhecido'}.`;
        }
      }

      const successCount = createdIds.length;
      toast({
        title: 'Upload finalizado',
        description: `${successCount} infração(ões) enviada(s).${failed > 0 ? ` ${failed} falha(s).` : ''}${batchesMessage}${successCount > 0 ? ' A leitura de placa (ALPR) será feita em fila nas máquinas cadastradas.' : ''}`,
        status: successCount > 0 ? 'success' : 'error',
        duration: 6000,
        isClosable: true
      });

      if (successCount > 0) {
        clearSelection();
      }
    } catch (error) {
      toast({
        title: 'Erro no upload',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleStartUpload = () => {
    if (!cameraId) {
      toast({
        title: 'Selecione uma câmera',
        status: 'warning',
        duration: 2500,
        isClosable: true
      });
      return;
    }

    if (items.length === 0) {
      toast({
        title: 'Selecione imagens',
        status: 'warning',
        duration: 2500,
        isClosable: true
      });
      return;
    }

    if (missingDateItems.length > 0) {
      setMissingDateDialogOpen(true);
      return;
    }

    runUpload(items);
  };

  const handleFillDatesNow = () => {
    setMissingDateDialogOpen(false);
    const queueIds = missingDateItems.map((item) => item.id);
    setManualQueueIds(queueIds);
    setManualDateMode(true);
    setManualDateIndex(0);
    setManualDateValue('');
  };

  const handleUseCurrentDatetime = () => {
    setMissingDateDialogOpen(false);
    toast({
      title: 'Data/hora atual será usada',
      description: `${missingDateItems.length} imagem(ns) sem data no nome usarão a data e hora atuais no envio.`,
      status: 'info',
      duration: 4500,
      isClosable: true
    });

    const prepared = items.map((item) => (
      item.detectedAt
        ? item
        : { ...item, detectedAt: new Date() }
    ));
    runUpload(prepared);
  };

  const saveManualDateAndAdvance = () => {
    const parsed = fromDatetimeLocalValue(manualDateValue);
    if (!parsed) {
      toast({
        title: 'Informe data e hora válidas',
        status: 'warning',
        duration: 2500,
        isClosable: true
      });
      return;
    }

    if (!currentManualItem) return;

    const nextItems = items.map((item) => (
      item.id === currentManualItem.id
        ? { ...item, detectedAt: parsed }
        : item
    ));
    setItems(nextItems);

    const nextIndex = manualDateIndex + 1;
    if (nextIndex >= manualQueueIds.length) {
      setManualDateMode(false);
      setManualQueueIds([]);
      setManualDateIndex(0);
      setManualDateValue('');
      toast({
        title: 'Datas preenchidas',
        description: 'Iniciando envio das infrações...',
        status: 'success',
        duration: 2500,
        isClosable: true
      });
      runUpload(nextItems);
      return;
    }

    setManualDateIndex(nextIndex);
    setManualDateValue('');
  };

  const progressPercent = uploadProgress.total > 0
    ? Math.round((uploadProgress.done / uploadProgress.total) * 100)
    : 0;

  return (
    <Box>
      <Alert status="warning" mb={4} borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle fontSize="sm">Uma câmera por vez</AlertTitle>
          <AlertDescription fontSize="sm">
            Faça upload apenas de imagens da mesma câmera selecionada. Misturar câmeras no mesmo envio
            pode gerar lotes e registros incorretos.
          </AlertDescription>
        </Box>
      </Alert>

      <Card mb={4}>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Heading size="md">Upload de infrações</Heading>
            <Text fontSize="sm" color="gray.600">
              Envie imagens sem limite de quantidade. A data/hora é lida do nome do arquivo quando possível
              (ex.: <Text as="span" fontFamily="mono">(02-09-2026_14-01-54.217)</Text>).
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Câmera</FormLabel>
                <CameraSearchSelect
                  cameras={cameras}
                  value={cameraId}
                  onChange={(e) => setCameraId(e.target.value)}
                  placeholder={loadingCameras ? 'Carregando câmeras...' : 'Selecione uma câmera'}
                  isDisabled={isUploading || loadingCameras}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Estado inicial</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  isDisabled={isUploading}
                  bg="white"
                >
                  <option value="pending">Pendente</option>
                  <option value="confirmed">Confirmada</option>
                  <option value="rejected">Cancelada</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <Checkbox
                isChecked={createBatches}
                onChange={(e) => setCreateBatches(e.target.checked)}
                isDisabled={isUploading || status !== 'pending'}
              >
                Criar lotes com essas infrações para a câmera selecionada
              </Checkbox>
              <FormHelperText>
                {status !== 'pending'
                  ? 'Disponível apenas quando o estado inicial for Pendente.'
                  : createBatches
                    ? `Os lotes serão gerados após o envio, com ${resolveBatchSize(batchSizePreset, customBatchSize)} infrações em cada lote. O último lote pode ficar menor.`
                    : 'Os lotes serão gerados após o envio, agrupando as infrações pendentes enviadas.'}
              </FormHelperText>
            </FormControl>

            {createBatches && status === 'pending' && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Infrações por lote</FormLabel>
                  <Select
                    value={batchSizePreset}
                    onChange={(e) => setBatchSizePreset(e.target.value)}
                    isDisabled={isUploading}
                    bg="white"
                  >
                    {BATCH_SIZE_PRESETS.map((size) => (
                      <option key={size} value={String(size)}>
                        {size} infrações por lote
                      </option>
                    ))}
                    <option value="custom">Outra quantidade</option>
                  </Select>
                </FormControl>
                {batchSizePreset === 'custom' && (
                  <FormControl>
                    <FormLabel>Quantidade</FormLabel>
                    <NumberInput
                      min={MIN_BATCH_SIZE}
                      max={MAX_BATCH_SIZE}
                      value={customBatchSize}
                      onChange={(value) => setCustomBatchSize(value)}
                      isDisabled={isUploading}
                    >
                      <NumberInputField bg="white" />
                    </NumberInput>
                    <FormHelperText>De {MIN_BATCH_SIZE} a {MAX_BATCH_SIZE} infrações em cada lote.</FormHelperText>
                  </FormControl>
                )}
              </SimpleGrid>
            )}

            <HStack spacing={3} flexWrap="wrap">
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/bmp,.jpg,.jpeg,.png,.webp,.bmp"
                multiple
                display="none"
                onChange={handleFilesSelected}
                disabled={isUploading}
              />
              <Button
                colorScheme="blue"
                onClick={() => fileInputRef.current?.click()}
                isDisabled={isUploading}
              >
                Selecionar imagens
              </Button>
              <Button
                variant="outline"
                onClick={clearSelection}
                isDisabled={isUploading || items.length === 0}
              >
                Limpar seleção
              </Button>
              <Button
                colorScheme="green"
                onClick={handleStartUpload}
                isLoading={isUploading}
                loadingText="Enviando"
                isDisabled={items.length === 0 || !cameraId}
              >
                Enviar {items.length > 0 ? `(${items.length})` : ''}
              </Button>
            </HStack>

            {items.length > 0 && (
              <Box bg="gray.50" p={3} borderRadius="md">
                <Text fontSize="sm">
                  {items.length} imagem(ns) selecionada(s) — {parsedCount} com data no nome
                  {missingDateItems.length > 0 ? `, ${missingDateItems.length} sem data/hora` : ''}.
                </Text>
              </Box>
            )}

            {(isUploading || uploadProgress.total > 0) && (
              <Box>
                <Flex justify="space-between" mb={1}>
                  <Text fontSize="sm" fontWeight="medium">Progresso do envio</Text>
                  <Text fontSize="sm">
                    {uploadProgress.done}/{uploadProgress.total} ({progressPercent}%)
                  </Text>
                </Flex>
                <Progress
                  value={progressPercent}
                  size="md"
                  colorScheme="green"
                  borderRadius="md"
                  hasStripe={isUploading}
                  isAnimated={isUploading}
                />
                {uploadProgress.failed > 0 && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    {uploadProgress.failed} falha(s) neste envio
                  </Text>
                )}
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      {manualDateMode && currentManualItem && (
        <Card mb={4} borderColor="orange.300" borderWidth="1px">
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Heading size="sm">
                Informar data/hora ({manualDateIndex + 1} de {manualQueueIds.length})
              </Heading>
              <Text fontSize="sm" color="gray.600">{currentManualItem.filename}</Text>
              {currentManualItem.previewUrl && (
                <Image
                  src={currentManualItem.previewUrl}
                  alt={currentManualItem.filename}
                  maxH="240px"
                  objectFit="contain"
                  borderRadius="md"
                  bg="black"
                />
              )}
              <FormControl isRequired>
                <FormLabel>Data e hora da infração</FormLabel>
                <Input
                  type="datetime-local"
                  step="1"
                  value={manualDateValue}
                  onChange={(e) => setManualDateValue(e.target.value)}
                />
              </FormControl>
              <HStack>
                <Button colorScheme="orange" onClick={saveManualDateAndAdvance}>
                  {manualDateIndex + 1 >= manualQueueIds.length ? 'Concluir datas' : 'Salvar e próxima'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setManualDateMode(false);
                    setManualQueueIds([]);
                    setManualDateIndex(0);
                  }}
                >
                  Cancelar
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      )}

      {items.length > 0 && !manualDateMode && (
        <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={3}>
          {items.slice(0, 24).map((item) => (
            <Box
              key={item.id}
              borderWidth="1px"
              borderRadius="md"
              overflow="hidden"
              bg="white"
            >
              <Image src={item.previewUrl} alt={item.filename} h="90px" w="100%" objectFit="cover" />
              <Box p={2}>
                <Text fontSize="xs" noOfLines={1} title={item.filename}>{item.filename}</Text>
                <Text fontSize="xs" color={item.detectedAt ? 'green.600' : 'orange.600'}>
                  {item.detectedAt
                    ? item.detectedAt.toLocaleString('pt-BR')
                    : 'Sem data no nome'}
                </Text>
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {items.length > 24 && !manualDateMode && (
        <Text fontSize="sm" color="gray.500" mt={2}>
          Mostrando 24 de {items.length} pré-visualizações.
        </Text>
      )}

      <AlertDialog
        isOpen={missingDateDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setMissingDateDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Data/hora não encontrada
            </AlertDialogHeader>
            <AlertDialogBody>
              Não foi possível extrair data e hora do nome de {missingDateItems.length} imagem(ns).
              Deseja inserir esses dados agora?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleUseCurrentDatetime} mr={3}>
                Não, usar data/hora atual
              </Button>
              <Button colorScheme="orange" onClick={handleFillDatesNow}>
                Sim, inserir agora
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default UploadInfractionsTab;
